import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react'
import { Song } from '../utils/redux/playlistSlice'
import api from '../utils/api'

export interface AudioPlayerContextType {
  currentSong: Song | null
  queue: Song[]
  currentIndex: number
  isPlaying: boolean
  volume: number
  duration: number
  currentTime: number
  isLoading: boolean
  playSong: (song: Song, songQueue?: Song[]) => void
  playQueue: (songs: Song[], startIndex?: number) => void
  togglePlayPause: () => void
  playNext: () => void
  playPrevious: () => void
  setVolume: (volume: number) => void
  seek: (time: number) => void
  clearQueue: () => void
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined)

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext)
  if (!context) throw new Error('useAudioPlayer must be used within AudioPlayerProvider')
  return context
}

interface AudioPlayerProviderProps {
  children: React.ReactNode
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const [currentSong, setCurrentSong] = useState<Song | null>(null)
  const [queue, setQueue] = useState<Song[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolumeState] = useState(1)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({})
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // ✅ refs, чтобы next/prev/ended всегда видели актуальные значения
  const queueRef = useRef<Song[]>([])
  const indexRef = useRef<number>(0)
  const loadTokenRef = useRef(0)

  useEffect(() => {
    queueRef.current = queue
  }, [queue])

  useEffect(() => {
    indexRef.current = currentIndex
  }, [currentIndex])

  // init audio once
  useEffect(() => {
    const audio = new Audio()
    audioRef.current = audio

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0)
    const handleDurationChange = () => setDuration(audio.duration || 0)
    const handleVolumeChange = () => setVolumeState(audio.volume)

    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('volumechange', handleVolumeChange)

    return () => {
      audio.pause()
      audio.src = ''
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('volumechange', handleVolumeChange)

      Object.values(audioUrls).forEach((url) => URL.revokeObjectURL(url))
    }
  }, [])

  const fetchAudioBlob = useCallback(
    async (song: Song): Promise<string> => {
      if (audioUrls[song._id]) return audioUrls[song._id]

      const response = await api.get(`/song/download/${(song as any).file}`, {
        responseType: 'blob',
      })

      if (!response.data || response.data.size === 0) {
        throw new Error('Received empty audio file')
      }

      const blobUrl = URL.createObjectURL(response.data)
      setAudioUrls((prev) => ({ ...prev, [song._id]: blobUrl }))
      return blobUrl
    },
    [audioUrls],
  )

  const loadAndPlaySong = useCallback(
    async (song: Song) => {
      const audio = audioRef.current
      if (!audio) return

      const myToken = ++loadTokenRef.current
      setIsLoading(true)

      try {
        audio.pause()

        const blobUrl = await fetchAudioBlob(song)
        if (myToken !== loadTokenRef.current) return

        audio.src = blobUrl
        audio.currentTime = 0
        audio.load()

        await audio.play()
        if (myToken !== loadTokenRef.current) return

        setCurrentSong(song)
      } catch (error) {
        console.error('Error loading song:', error)
      } finally {
        if (myToken === loadTokenRef.current) setIsLoading(false)
      }
    },
    [fetchAudioBlob],
  )

  // ✅ replace queue полностью (как в ТЗ)
  const playQueue = useCallback(
    (songs: Song[], startIndex: number = 0) => {
      if (!songs || songs.length === 0) return

      const safeIndex = Math.max(0, Math.min(startIndex, songs.length - 1))
      setQueue(songs)
      setCurrentIndex(safeIndex)
      setCurrentSong(songs[safeIndex])
      loadAndPlaySong(songs[safeIndex])
    },
    [loadAndPlaySong],
  )

  const playSong = useCallback(
    (song: Song, songQueue?: Song[]) => {
      if (songQueue?.length) {
        const idx = songQueue.findIndex((s) => s._id === song._id)
        playQueue(songQueue, idx >= 0 ? idx : 0)
      } else {
        playQueue([song], 0)
      }
    },
    [playQueue],
  )

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (!audio.src) return

    if (audio.paused) audio.play().catch(console.error)
    else audio.pause()
  }, [])

  const playAtIndex = useCallback(
    (idx: number) => {
      const q = queueRef.current
      if (!q.length) return
      const safe = Math.max(0, Math.min(idx, q.length - 1))
      setCurrentIndex(safe)
      setCurrentSong(q[safe])
      loadAndPlaySong(q[safe])
    },
    [loadAndPlaySong],
  )

  const playNext = useCallback(() => {
    const q = queueRef.current
    if (q.length === 0) return

    const nextIndex = indexRef.current + 1
    if (nextIndex < q.length) playAtIndex(nextIndex)
    else playAtIndex(0) // loop to start
  }, [playAtIndex])

  const playPrevious = useCallback(() => {
    const q = queueRef.current
    if (q.length === 0) return

    const audio = audioRef.current
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0
      return
    }

    const prevIndex = indexRef.current - 1
    if (prevIndex >= 0) playAtIndex(prevIndex)
    else playAtIndex(q.length - 1) // loop to end
  }, [playAtIndex])

  const setVolume = useCallback((newVolume: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = Math.max(0, Math.min(1, newVolume))
  }, [])

  const seek = useCallback((time: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = time
  }, [])

  const clearQueue = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.src = ''
    }
    loadTokenRef.current++ // сбросить текущие загрузки

    setQueue([])
    setCurrentSong(null)
    setCurrentIndex(0)
    setIsPlaying(false)
    setDuration(0)
    setCurrentTime(0)
    setIsLoading(false)

    // чистим blob urls (logout)
    Object.values(audioUrls).forEach((url) => URL.revokeObjectURL(url))
    setAudioUrls({})
  }, [audioUrls])

  // ✅ ended = next
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleEnded = () => {
      const q = queueRef.current
      if (!q.length) {
        setIsPlaying(false)
        return
      }
      const nextIndex = indexRef.current + 1
      if (nextIndex < q.length) playAtIndex(nextIndex)
      else {
        // закончилась очередь — можно остановить или зациклить.
        // ты хотел "подряд" — обычно loop на начало:
        playAtIndex(0)
      }
    }

    audio.addEventListener('ended', handleEnded)
    return () => audio.removeEventListener('ended', handleEnded)
  }, [playAtIndex])

  const value: AudioPlayerContextType = {
    currentSong,
    queue,
    currentIndex,
    isPlaying,
    volume,
    duration,
    currentTime,
    isLoading,
    playSong,
    playQueue,
    togglePlayPause,
    playNext,
    playPrevious,
    setVolume,
    seek,
    clearQueue,
  }

  return (
    <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>
  )
}
